// @ts-nocheck
/**
 * Business Info Tab - Informações do Negócio (Admin only)
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";

export function BusinessInfoTab() {
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);

  const { data: config, isLoading } = useQuery({
    queryKey: ["business-config"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("configuracoes_negocio")
        .select("*")
        .single() as any;
      if (error) throw error;
      return data;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (values: any) => {
      const { error } = await supabase
        .from("configuracoes_negocio")
        .update({
          ...values,
          updated_at: new Date().toISOString(),
        })
        .eq("id", config?.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business-config"] });
      toast.success("✅ Configurações salvas com sucesso!");
    },
    onError: () => {
      toast.error("❌ Erro ao salvar configurações");
    },
  });

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Arquivo muito grande. Máximo 2MB");
      return;
    }

    setUploading(true);
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `logo-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("business-logos")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("business-logos")
        .getPublicUrl(fileName);

      await updateMutation.mutateAsync({ logo_url: publicUrl });
      toast.success("📸 Logo enviado com sucesso!");
    } catch (error) {
      toast.error("❌ Erro ao enviar logo");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const values = {
      nome_confeitaria: formData.get("nome_confeitaria"),
      responsavel: formData.get("responsavel"),
      cnpj_cpf: formData.get("cnpj_cpf"),
      telefone: formData.get("telefone"),
      email_comercial: formData.get("email_comercial"),
      endereco_completo: formData.get("endereco_completo"),
      cor_principal: formData.get("cor_principal"),
      cor_destaque: formData.get("cor_destaque"),
    };
    updateMutation.mutate(values);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>💼 Informações do Negócio</CardTitle>
        <CardDescription>
          Configure os dados da sua confeitaria (visível apenas para administradores)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nome_confeitaria">Nome da Confeitaria *</Label>
              <Input
                id="nome_confeitaria"
                name="nome_confeitaria"
                defaultValue={config?.nome_confeitaria}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="responsavel">Responsável</Label>
              <Input
                id="responsavel"
                name="responsavel"
                defaultValue={config?.responsavel || ""}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cnpj_cpf">CNPJ / CPF</Label>
              <Input
                id="cnpj_cpf"
                name="cnpj_cpf"
                defaultValue={config?.cnpj_cpf || ""}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="telefone">Telefone / WhatsApp</Label>
              <Input
                id="telefone"
                name="telefone"
                type="tel"
                defaultValue={config?.telefone || ""}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email_comercial">E-mail Comercial</Label>
              <Input
                id="email_comercial"
                name="email_comercial"
                type="email"
                defaultValue={config?.email_comercial || ""}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="endereco_completo">Endereço Completo</Label>
            <Textarea
              id="endereco_completo"
              name="endereco_completo"
              defaultValue={config?.endereco_completo || ""}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label>Logo da Marca</Label>
            <div className="flex items-center gap-4">
              {config?.logo_url && (
                <img
                  src={config.logo_url}
                  alt="Logo"
                  className="w-20 h-20 object-contain rounded border"
                />
              )}
              <div>
                <Input
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml"
                  onChange={handleLogoUpload}
                  disabled={uploading}
                  className="max-w-xs"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  PNG, JPG ou SVG. Máximo 2MB
                </p>
              </div>
              {uploading && <Loader2 className="w-4 h-4 animate-spin" />}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cor_principal">Cor Principal do Tema</Label>
              <div className="flex gap-2">
                <Input
                  id="cor_principal"
                  name="cor_principal"
                  type="color"
                  defaultValue={config?.cor_principal}
                  className="w-20 h-10"
                />
                <Input
                  type="text"
                  defaultValue={config?.cor_principal}
                  className="flex-1"
                  readOnly
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cor_destaque">Cor de Destaque</Label>
              <div className="flex gap-2">
                <Input
                  id="cor_destaque"
                  name="cor_destaque"
                  type="color"
                  defaultValue={config?.cor_destaque}
                  className="w-20 h-10"
                />
                <Input
                  type="text"
                  defaultValue={config?.cor_destaque}
                  className="flex-1"
                  readOnly
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Salvar Alterações
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
